import { useEffect, useState } from 'react';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationModal from '../components/ConfirmationModal';

export default function FeatureFlagsPage() {
  const { flags, loading, error, fetchFlags, toggleFlag } = useFeatureFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, enabled, disabled
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    flag: null,
    action: null, // 'toggle'
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Filter flags based on search and status
  const filteredFlags = flags.filter((flag) => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      flag.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      flag.description?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'enabled' && flag.enabled) ||
      (statusFilter === 'disabled' && !flag.enabled);

    return matchesSearch && matchesStatus;
  });

  const handleToggle = (flag) => {
    setConfirmModal({
      isOpen: true,
      flag,
      action: 'toggle',
    });
  };

  const handleConfirmToggle = async () => {
    /* c8 ignore start */
    if (!confirmModal.flag) return;
    /* c8 ignore stop */

    /* c8 ignore start */
    setIsProcessing(true);
    try {
      await toggleFlag(confirmModal.flag.key);
      setConfirmModal({ isOpen: false, flag: null, action: null });
    } catch (err) {
      console.error('Failed to toggle feature flag:', err);
    } finally {
      setIsProcessing(false);
    }
    /* c8 ignore stop */
  };

  const handleCloseModal = () => {
    /* c8 ignore start */
    if (!isProcessing) {
      setConfirmModal({ isOpen: false, flag: null, action: null });
    }
    /* c8 ignore stop */
  };

  if (loading && flags.length === 0) {
    return <LoadingSpinner message="Loading feature flags..." />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        <p className="font-medium">Error loading feature flags</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Feature Flags</h1>
          <p className="text-gray-400 mt-1">View and manage feature flag settings</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="search"
            placeholder="Search by key or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Search feature flags"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="enabled">Enabled Only</option>
            <option value="disabled">Disabled Only</option>
          </select>
        </div>
      </div>

      {/* Empty state */}
      {filteredFlags.length === 0 && flags.length === 0 && (
        <div className="text-center py-12 bg-dark-800 border border-dark-700 rounded-lg">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-700 mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No feature flags found</h3>
          <p className="text-gray-400">Feature flags are configured at the system level.</p>
        </div>
      )}

      {/* No results from search/filter */}
      {filteredFlags.length === 0 && flags.length > 0 && (
        <div className="text-center py-12 bg-dark-800 border border-dark-700 rounded-lg">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-700 mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No matching feature flags</h3>
          <p className="text-gray-400">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Feature flags table */}
      {filteredFlags.length > 0 && (
        <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
          <table className="w-full" aria-label="Feature flags list">
            <caption className="sr-only">
              List of feature flags with key, description, and enabled status.
            </caption>
            <thead className="bg-dark-700 border-b border-dark-600">
              <tr>
                <th className="px-6 py-3 text-left">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Key
                  </span>
                </th>
                <th className="px-6 py-3 text-left">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Description
                  </span>
                </th>
                <th className="px-6 py-3 text-left">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </span>
                </th>
                <th className="px-6 py-3 text-left">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Last Updated
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {filteredFlags.map((flag) => (
                <tr key={flag.key} className="hover:bg-dark-750 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono font-medium text-white">{flag.key}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">{flag.description || 'No description'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(flag)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        flag.enabled
                          ? 'bg-green-900/30 text-green-400 border border-green-500/30 hover:bg-green-900/40'
                          : 'bg-gray-900/30 text-gray-400 border border-gray-500/30 hover:bg-gray-900/40'
                      }`}
                      aria-label={`Toggle ${flag.key} flag: currently ${flag.enabled ? 'enabled' : 'disabled'}`}
                      aria-pressed={flag.enabled}
                      role="switch"
                    >
                      {flag.enabled ? '● Enabled' : '○ Disabled'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-400">
                      {flag.updated_at ? new Date(flag.updated_at).toLocaleDateString() : 'Never'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Results count */}
      {filteredFlags.length > 0 && (
        <div className="mt-4 text-sm text-gray-400">
          Showing {filteredFlags.length} of {flags.length} feature flag{flags.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Confirmation Modal (Toggle) */}
      {confirmModal.action === 'toggle' && confirmModal.flag && (
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmToggle}
          title={confirmModal.flag.enabled ? 'Disable Feature Flag' : 'Enable Feature Flag'}
          message={
            confirmModal.flag.enabled
              ? `Are you sure you want to disable "${confirmModal.flag.key}"? This feature will be turned off for all users.`
              : `Are you sure you want to enable "${confirmModal.flag.key}"? This feature will be turned on for all users.`
          }
          confirmText={confirmModal.flag.enabled ? 'Disable' : 'Enable'}
          cancelText="Cancel"
          confirmVariant={confirmModal.flag.enabled ? 'danger' : 'primary'}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}
